import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  formatBasisPoints,
  formatCurrency,
  formatPercentage,
} from "../../utils/dataCalculations";

const MetricCard = ({
  title,
  value,
  subtitle,
  type = "currency", // 'currency', 'percentage', 'basisPoints', 'text'
  color = "primary", // 'primary', 'success', 'error', 'warning', 'info'
  showTrend = false,
  trendValue = 0,
  trendDirection = "up", // 'up', 'down', 'neutral'
  size = "medium", // 'small', 'medium', 'large'
}) => {
  const theme = useTheme();

  const getColorByType = () => {
    switch (color) {
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
      case "info":
        return theme.palette.info.main;
      default:
        return theme.palette.primary.dark;
    }
  };

  const getBackgroundColorByType = () => {
    switch (color) {
      case "success":
        return theme.palette.success.light + "20";
      case "error":
        return theme.palette.error.light + "20";
      case "warning":
        return theme.palette.warning.light + "20";
      case "info":
        return theme.palette.info.light + "20";
      default:
        return theme.palette.primary.light + "20";
    }
  };

  const formatValue = () => {
    switch (type) {
      case "currency":
        return formatCurrency(value);
      case "percentage":
        return formatPercentage(value);
      case "basisPoints":
        return formatBasisPoints(value);
      case "text":
      default:
        return value;
    }
  };

  const getFontSize = () => {
    switch (size) {
      case "small":
        return { xs: "20px", md: "22px" };
      case "large":
        return { xs: "28px", md: "32px" };
      default:
        return { xs: "24px", md: "28px" };
    }
  };

  const getPadding = () => {
    switch (size) {
      case "small":
        return 2;
      case "large":
        return 4;
      default:
        return 3;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: getPadding(),
        borderRadius: "16px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[200]}`,
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
          borderColor: theme.palette.grey[300],
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: "14px",
          fontWeight: "600",
          color: theme.palette.text.secondary,
          mb: 1,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontSize(),
          fontWeight: "700",
          color: getColorByType(),
          mb: 1,
          lineHeight: 1.2,
        }}
      >
        {formatValue()}
      </Typography>

      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: getBackgroundColorByType(),
          color: getColorByType(),
          px: 2,
          py: 0.5,
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        {subtitle}
      </Box>

      {showTrend && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 1,
            fontSize: "12px",
            color:
              trendDirection === "up"
                ? theme.palette.success.main
                : trendDirection === "down"
                ? theme.palette.error.main
                : theme.palette.text.secondary,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "inherit",
              fontWeight: "600",
            }}
          >
            {trendDirection === "up"
              ? "↗"
              : trendDirection === "down"
              ? "↘"
              : "→"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "inherit",
              fontWeight: "600",
            }}
          >
            {formatBasisPoints(trendValue)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MetricCard;
